import { useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// Map route paths to module IDs
const ROUTE_TO_MODULE: Record<string, string> = {
  "/dashboard": "dashboard",
  "/": "dashboard",
  "/methodology": "methodology",
  "/mental-health-dimension": "mental-health",
  "/workforce-education": "workforce",
  "/entrepreneurship-support": "entrepreneurship",
  "/regional-analysis": "regional",
  "/member-insights": "members",
  "/programs-events": "programs",
  "/technology-future": "technology",
  "/vision-2040": "vision-2040",
  "/impact-metrics": "impact-metrics",
  "/development-councils": "development-councils",
  "/contributing": "contributing",
  "/faq": "faq",
};

export function useModuleVisitTracker() {
  const { pathname } = useLocation();
  const { user, profile } = useAuth();
  const trackedInSession = useRef<Set<string>>(new Set());

  const trackVisit = useCallback(
    async (moduleId: string) => {
      if (!user || !profile) return;

      // Already tracked in this session
      if (trackedInSession.current.has(moduleId)) return;

      // Already in visited_modules
      const currentModules = (profile as any).visited_modules ?? [];
      if (currentModules.includes(moduleId)) {
        trackedInSession.current.add(moduleId);
        return;
      }

      // Mark as tracked to prevent duplicate calls
      trackedInSession.current.add(moduleId);

      try {
        const updatedModules = [...currentModules, moduleId];
        
        // Calculate new mastery level
        const totalModules = Object.keys(ROUTE_TO_MODULE).length - 1; // -1 for "/" duplicate
        const visitedCount = updatedModules.length;
        const percentage = (visitedCount / totalModules) * 100;
        
        let masteryLevel = "observer";
        if (percentage >= 67) {
          masteryLevel = "visionary";
        } else if (percentage >= 34) {
          masteryLevel = "analyst";
        }

        await (supabase as any)
          .from("profiles")
          .update({
            visited_modules: updatedModules,
            mastery_level: masteryLevel,
          })
          .eq("id", user.id);

        console.log(`[ModuleTracker] Tracked visit to: ${moduleId} (${masteryLevel})`);
      } catch (error) {
        console.error("[ModuleTracker] Failed to track visit:", error);
        // Remove from session tracking so it can retry
        trackedInSession.current.delete(moduleId);
      }
    },
    [user, profile]
  );

  useEffect(() => {
    const moduleId = ROUTE_TO_MODULE[pathname];
    if (moduleId) {
      trackVisit(moduleId);
    }
  }, [pathname, trackVisit]);

  return {
    visitedModules: (profile as any)?.visited_modules ?? [],
    masteryLevel: (profile as any)?.mastery_level ?? "observer",
  };
}
